import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Card, Badge } from "../components/ui";

const STATUS_VARIANTS = {
  success: "success",
  error: "danger",
  skipped: "warning",
  duplicate_skipped: "info",
};

const STATUS_LABELS = {
  success: "OK",
  error: "Errore",
  skipped: "Saltata",
  duplicate_skipped: "Duplicata",
};

function RunSummaryCard({ run }) {
  const [expanded, setExpanded] = useState(false);
  const ts = run.timestamp
    ? new Date(run.timestamp).toLocaleString("it-IT")
    : "—";
  const durSec = run.durationMs ? Math.round(run.durationMs / 1000) : null;
  const hasIssues = run.errors > 0 || run.unclassified > 0;

  return (
    <Card
      className={`p-4 border-l-4 ${hasIssues ? "border-l-red-400" : run.unclassified > 0 ? "border-l-purple-400" : "border-l-green-400"}`}
    >
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-gray-700">{ts}</span>
          {durSec !== null && (
            <span className="text-xs text-gray-400">{durSec}s</span>
          )}
          <span className="text-sm text-gray-600">
            {run.processed ?? 0} processate
          </span>
          <Badge variant="success">{run.success ?? 0} ok</Badge>
          {run.errors > 0 && (
            <Badge variant="danger">{run.errors} errori</Badge>
          )}
          {run.unclassified > 0 && (
            <Badge variant="warning">{run.unclassified} non classificate</Badge>
          )}
          {run.skipped > 0 && (
            <Badge variant="default">{run.skipped} skip</Badge>
          )}
        </div>
        <span className="text-gray-400 text-sm ml-2">
          {expanded ? "▲" : "▼"}
        </span>
      </div>

      {expanded && (run.details || []).length > 0 && (
        <div className="mt-3 space-y-2 border-t pt-3">
          {run.details.map((d, i) => (
            <div
              key={i}
              className="text-sm bg-gray-50 rounded p-2 flex flex-col gap-0.5"
            >
              <div className="flex items-center gap-2">
                <Badge variant={STATUS_VARIANTS[d.status] || "default"}>
                  {STATUS_LABELS[d.status] || d.status}
                </Badge>
                {d.reason === "no_mapping_for_sender" && (
                  <Badge variant="warning">Senza mapping</Badge>
                )}
                <span className="text-gray-500 truncate max-w-xs">
                  {d.from || "—"}
                </span>
              </div>
              <span className="text-gray-700 truncate">{d.subject || "—"}</span>
              {d.error && (
                <span className="text-red-600 text-xs">{d.error}</span>
              )}
              {(d.actions || [])
                .filter((a) => a.type === "process_bolletta")
                .map((a, j) => (
                  <div key={j} className="text-xs text-gray-500">
                    Bolletta: {a.importo != null ? `€${a.importo}` : "—"} ·
                    confidence {a.confidence ?? "?"}%
                    {!a.contatoreId && (
                      <span className="text-orange-500 ml-1">
                        · senza contatore
                      </span>
                    )}
                  </div>
                ))}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function EmailProcessingLogPage() {
  const { apiRequest, user } = useAuth();
  const { showToast } = useToast();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const isAdmin = user?.ruolo === 100000000;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiRequest("/email-processing/history");
      setHistory(data.value || []);
    } catch {
      showToast("Errore caricamento log", "error");
    } finally {
      setLoading(false);
    }
  }, [apiRequest, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRun = async () => {
    setRunning(true);
    try {
      await apiRequest("/email-processing/run", { method: "POST" });
      showToast("Elaborazione email avviata in background", "success");
      setTimeout(load, 5000);
    } catch {
      showToast("Errore avvio elaborazione", "error");
    } finally {
      setRunning(false);
    }
  };

  const totalUnclassified = history.reduce(
    (s, r) => s + (r.unclassified || 0),
    0,
  );
  const totalErrors = history.reduce((s, r) => s + (r.errors || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Log Elaborazione Email Bollette
          </h1>
          <p className="text-gray-500 mt-1">
            Storico degli ultimi {history.length} cicli di elaborazione
            automatica
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading}>
            {loading ? "Aggiornamento..." : "Aggiorna"}
          </Button>
          {isAdmin && (
            <Button onClick={handleRun} disabled={running}>
              {running ? "In corso..." : "Esegui ora"}
            </Button>
          )}
        </div>
      </div>

      {(totalErrors > 0 || totalUnclassified > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {totalErrors > 0 && (
            <Card className="p-4 bg-red-50 border-red-200">
              <p className="text-sm text-red-600 font-medium">
                Errori totali (ultimi {history.length} run)
              </p>
              <p className="text-2xl font-bold text-red-700">{totalErrors}</p>
            </Card>
          )}
          {totalUnclassified > 0 && (
            <Card className="p-4 bg-purple-50 border-purple-200">
              <p className="text-sm text-purple-600 font-medium">
                Email non classificate
              </p>
              <p className="text-2xl font-bold text-purple-700">
                {totalUnclassified}
              </p>
              <p className="text-xs text-purple-500 mt-1">
                Spostate in "Bollette Non Classificate" su Outlook — configurare
                mapping in Amministrazione
              </p>
            </Card>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500 text-center py-8">Caricamento...</p>
      ) : history.length === 0 ? (
        <Card className="p-8 text-center text-gray-500">
          Nessun run registrato. Il log si popola ad ogni ciclo di elaborazione
          email (ogni ora).
        </Card>
      ) : (
        <div className="space-y-3">
          {history.map((run, i) => (
            <RunSummaryCard key={i} run={run} />
          ))}
        </div>
      )}
    </div>
  );
}
